import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import { puedeUsarExcluirEmpleados } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';

export { puedeUsarExcluirEmpleados };

const btnBase = { textTransform: 'none', borderRadius: 1.8, fontWeight: 700, boxShadow: 'none' };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: '#FFFFFF',
  border: `1px solid ${c}`,
  '&:hover': { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
  '&.Mui-disabled': { color: FP.color.textMuted, borderColor: FP.color.border },
});
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: '#FFFFFF',
  '&:hover': { bgcolor: hover, boxShadow: 'none', color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: `${bg}66`, color: '#FFFFFF' },
});

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const radioSx = {
  color: FP.color.navy,
  '&.Mui-checked': { color: FP.color.navy },
};

/**
 * Excluir empleados de la nómina activa (fps9010).
 * Saca el registro de `pagosnom` o lo marca fuera del archivo de banco.
 */
const ExcluirEmpleadoNomina = ({ open = false, onClose, embedded = false }) => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, roleDetalles } = controller;
  const { post } = useApi();

  const puedeUsar = useMemo(() => puedeUsarExcluirEmpleados(roleDetalles), [roleDetalles]);
  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);

  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [empleados, setEmpleados] = useState([]);
  const [empleado, setEmpleado] = useState(null);
  const [accion, setAccion] = useState('');

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany],
  );

  const cargar = useCallback(async () => {
    if (!puedeUsar || !puedeOperar) {
      setEmpleados([]);
      return;
    }
    setCargando(true);
    try {
      const { ok, data } = await post('/api/nomina_excluirempleados', {
        ...credencialesEmpresa(),
        solo_catalogos: '1',
      });
      if (!ok) throw new Error(data.Error || 'No se pudieron leer los empleados');
      setEmpleados(Array.isArray(data.empleados) ? data.empleados : []);
    } catch (e) {
      setEmpleados([]);
      showError(e.message || 'No se pudieron leer los empleados');
    } finally {
      setCargando(false);
    }
  }, [puedeUsar, puedeOperar, post, credencialesEmpresa, showError]);

  useEffect(() => {
    if ((open || embedded) && puedeUsar && puedeOperar) cargar();
  }, [open, embedded, puedeUsar, puedeOperar, cargar]);

  const cerrar = () => {
    if (procesando) return;
    setEmpleado(null);
    setAccion('');
    if (onClose) onClose();
  };

  const procesar = async () => {
    if (!puedeUsar) {
      showError('No tiene permisos para excluir empleados');
      return;
    }
    if (!empleado?.numemp) {
      showError('El empleado es requerido');
      return;
    }
    if (!accion) {
      showError('Debe seleccionar una de las opciones para procesar empleado');
      return;
    }
    setProcesando(true);
    try {
      const { ok, data } = await post('/api/nomina_excluirempleados', {
        ...credencialesEmpresa(),
        numemp: empleado.numemp,
        accion,
      });
      if (!ok) throw new Error(data.Error || 'Problemas para guardar datos');
      showSuccess(data.ok || 'Dato procesado exitosamente');
      setEmpleados(Array.isArray(data.empleados) ? data.empleados : []);
      setEmpleado(null);
      setAccion('');
      if (onClose) onClose();
    } catch (e) {
      showError(e.message || 'Problemas para guardar datos');
    } finally {
      setProcesando(false);
    }
  };

  const cuerpo = !puedeOperar ? (
    <Typography sx={{ color: FP.color.textSecondary, py: 2 }}>
      Seleccione una empresa para excluir empleados.
    </Typography>
  ) : cargando ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress size={28} sx={{ color: FP.color.navy }} />
    </Box>
  ) : (
    <Box sx={{ mt: 1 }}>
      <Autocomplete
        options={empleados}
        value={empleado}
        onChange={(_, v) => setEmpleado(v)}
        getOptionLabel={(o) => o?.label || ''}
        isOptionEqualToValue={(a, b) => String(a?.numemp) === String(b?.numemp)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Empleado"
            placeholder="Buscar empleado de la nómina activa…"
            InputLabelProps={{ shrink: true }}
            sx={fpInputSx}
          />
        )}
      />

      <FormControl sx={{ mt: 2.5, display: 'block' }}>
        <RadioGroup value={accion} onChange={(e) => setAccion(e.target.value)}>
          <FormControlLabel
            value="sacar"
            control={<Radio sx={radioSx} />}
            label="Sacar empleado de nómina"
            sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: '0.9rem' } }}
          />
          <FormControlLabel
            value="banco"
            control={<Radio sx={radioSx} />}
            label="Solo excluir del archivo de banco"
            sx={{ '& .MuiFormControlLabel-label': { fontWeight: 600, fontSize: '0.9rem' } }}
          />
        </RadioGroup>
      </FormControl>

      <Typography sx={{ color: FP.color.textMuted, fontSize: '0.82rem', mt: 1.5 }}>
        {accion === 'sacar'
          ? 'Elimina al empleado del archivo de la nómina activa. No afecta el maestro ni nóminas históricas.'
          : accion === 'banco'
            ? 'El empleado permanece en la nómina, pero no se incluye en el archivo de banco.'
            : 'Elija si lo saca de la nómina activa o solo del archivo de banco.'}
      </Typography>
    </Box>
  );

  const acciones = (
    <>
      {onClose && (
        <Button onClick={cerrar} disabled={procesando} sx={btnOutline(FP.color.textSecondary)}>
          Cancelar
        </Button>
      )}
      <Button
        onClick={procesar}
        disabled={procesando || cargando || !puedeUsar || !puedeOperar || !empleado || !accion}
        startIcon={procesando ? <CircularProgress size={16} color="inherit" /> : <PlayArrowOutlinedIcon />}
        sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
      >
        {procesando ? 'Procesando…' : 'Aplicar'}
      </Button>
    </>
  );

  if (!puedeUsar) return null;

  if (embedded) {
    return (
      <Box>
        {cuerpo}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>{acciones}</Box>
      </Box>
    );
  }

  return (
    <Dialog
      open={Boolean(open)}
      onClose={cerrar}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>Excluir empleados</DialogTitle>
      <DialogContent>{cuerpo}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>{acciones}</DialogActions>
    </Dialog>
  );
};

export function ExcluirEmpleadoNominaPage() {
  const { controller } = useProductosMaterialContext();
  const { roleDetalles, nombreNegocio } = controller;
  const puedeUsar = useMemo(() => puedeUsarExcluirEmpleados(roleDetalles), [roleDetalles]);

  if (!puedeUsar) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
        <PageHeader title="Excluir empleados" subtitle="Nómina" />
        <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <LockOutlinedIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
              No tienes permisos para excluir empleados de la nómina.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Excluir empleados"
        subtitle={nombreNegocio ? `${nombreNegocio} · Nómina activa` : 'Nómina activa'}
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Card sx={cardShellSx}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <ExcluirEmpleadoNomina embedded open />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default ExcluirEmpleadoNomina;
